
import React, { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { Document, Page, Text, View, StyleSheet, Image, pdf } from '@react-pdf/renderer';
import { exportConsignmentToExcel } from "../../utils/excelExport";

// ─── Types ───────────────────────────────────────────────────────────────────
interface Company { id: number; company_name: string; address: string | null; password: string; pancard: string; city: string | null; state: string | null; country: string | null; gst_number: string | null; gst_uin: string | null; code: string | null; cin: string | null; email: string | null; created_at: string; updated_at: string; HSN_SAC: string; GST_SLAB: string | null; signature: string; prefix: string; postfix: string; separatorsign: string; }
interface User { id: number; name: string; email: string; number: string; password: string; role: string; deletedUser: number; createdAt: string; updatedAt: string; address: string | null; city: string | null; state: string | null; pincode: string | null; gst_number: string | null; pan_number: string | null; aadhaar_number: string | null; license_number: string | null; bank_name: string | null; bank_account_number: string | null; bank_ifsc: string | null; account_holder_name: string | null; }
interface Vehicle { id: number; vehicle_number: string; transporter_id?: number | null; driver_id: number; registration_number: string; vehicle_type: string | null; capacity_kg: number; }
interface DriverInput { name: string; phone: string; license_number: string; }
interface Transporter { id: number; company_name: string; }
interface Item { name: string; hsnCode?: string; qty: string; weight: string; ratePerQtl: string; amount: string; uom?: string; }
interface Note { id: string; text: string; color: string; }

// ─── Helpers ─────────────────────────────────────────────────────────────────
function fmtDate(iso: string) {
  if (!iso) return "";
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = String(d.getFullYear()).slice(-2);
  return `${dd}/${mm}/${yy}`;
}

function numberToWords(num: number): string {
  if (!num || num === 0) return '';
  const a = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const g = ['', 'Thousand', 'Lakh', 'Crore'];
  function toW(n: number): string {
    if (n < 20) return a[n];
    if (n < 100) return b[Math.floor(n / 10)] + (n % 10 ? ' ' + a[n % 10] : '');
    if (n < 1000) return a[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + toW(n % 100) : '');
    const chunks: number[] = [];
    chunks.push(n % 1000); let nn = Math.floor(n / 1000);
    while (nn > 0) { chunks.push(nn % 100); nn = Math.floor(nn / 100); }
    let res = '';
    for (let i = chunks.length - 1; i >= 0; i--)
      if (chunks[i]) res += (res ? ' ' : '') + toW(chunks[i]) + (g[i] ? ' ' + g[i] : '');
    return res.trim();
  }
  const parts = num.toFixed(2).split('.');
  let str = toW(parseInt(parts[0]));
  if (parseInt(parts[1]) > 0) str += ' and ' + toW(parseInt(parts[1])) + ' Paise';
  return str.toUpperCase() + ' ONLY /-';
}

function generateInvoiceId(origin: string, destination: string, vehicle_number: string | undefined, dateString: string) {
  return (origin?.substring(0, 2) || "OR").toUpperCase() +
    (destination?.substring(0, 2) || "DE").toUpperCase() +
    (vehicle_number ? vehicle_number.slice(-4) : "0000") +
    dateString.replace(/-/g, "");
}

function resolveItemAmount(item: Item): number {
  const qty = parseFloat(item.qty) || 0;
  const rate = parseFloat(item.ratePerQtl) || 0;
  const amt = parseFloat(item.amount) || 0;
  return amt > 0 ? amt : qty * rate;
}

// ─── Inline Field Dropdown ────────────────────────────────────────────────────
interface FieldDropdownProps<T> {
  label: string; value: T | null; options: T[]; getLabel: (item: T) => string;
  getSub?: (item: T) => string; onSelect: (item: T) => void; onAdd: () => void;
  placeholder?: string; className?: string;
}
function FieldDropdown<T extends { id: number }>({ label, value, options, getLabel, getSub, onSelect, onAdd, placeholder = "Click to select", className = "" }: FieldDropdownProps<T>) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h);
  }, []);
  const filtered = options.filter(o => getLabel(o).toLowerCase().includes(search.toLowerCase()) || (getSub?.(o) || "").toLowerCase().includes(search.toLowerCase()));
  return (
    <div ref={ref} className={`relative inline-block ${className}`}>
      <button type="button" onClick={() => { setOpen(v => !v); setSearch(""); }}
        className="group flex items-center gap-1 border-b border-dashed border-blue-400 hover:border-blue-700 transition-colors min-w-[80px] text-left">
        {value ? <span className="font-black text-black text-xs leading-tight">{getLabel(value)}</span>
          : <span className="text-blue-400 italic text-[10px]">{placeholder}</span>}
        <svg className="w-3 h-3 text-blue-400 ml-1 shrink-0" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><polyline points="6 9 12 15 18 9" /></svg>
      </button>
      {open && (
        <div className="absolute z-50 top-full left-0 mt-1 w-64 bg-white border-2 border-blue-400 rounded-lg shadow-xl overflow-hidden">
          <div className="p-2 border-b border-blue-100">
            <input autoFocus className="w-full text-xs px-2 py-1.5 border border-blue-200 rounded focus:outline-none focus:border-blue-500"
              placeholder={`Search ${label}...`} value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <div className="max-h-48 overflow-y-auto">
            {filtered.map(o => (
              <button key={o.id} type="button" onClick={() => { onSelect(o); setOpen(false); }}
                className={`w-full text-left px-3 py-2 hover:bg-blue-50 transition-colors ${value?.id === o.id ? "bg-blue-50 font-bold" : ""}`}>
                <div className="text-xs font-semibold text-gray-900">{getLabel(o)}</div>
                {getSub && <div className="text-[10px] text-gray-500">{getSub(o)}</div>}
              </button>
            ))}
            {filtered.length === 0 && <div className="px-3 py-2 text-[10px] text-gray-400 italic">No results found</div>}
          </div>
          <div className="border-t border-blue-100 p-1.5">
            <button type="button" onClick={() => { setOpen(false); onAdd(); }}
              className="w-full flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-blue-700 hover:bg-blue-50 rounded transition-colors">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
              Add new {label}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Inline Text Field ───────────────────────────────────────────────────────
function InlineEdit({ value, onChange, placeholder, className = "", type = "text", small = false }: {
  value: string; onChange: (v: string) => void; placeholder?: string; className?: string; type?: string; small?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);
  return editing ? (
    <input ref={inputRef} type={type}
      className={`border-b-2 border-blue-500 outline-none bg-transparent font-bold ${small ? "text-[10px] w-24" : "text-xs w-32"} ${className}`}
      value={value} onChange={e => onChange(e.target.value)} onBlur={() => setEditing(false)}
      onKeyDown={e => e.key === "Enter" && setEditing(false)} placeholder={placeholder} />
  ) : (
    // <span onClick={() => setEditing(true)}
    //   className={`cursor-text border-b border-dashed border-blue-400 hover:border-blue-700 transition-colors ${small ? "text-[10px]" : "text-xs"} font-bold ${!value ? "text-blue-400 italic font-normal" : "text-black"} ${className}`}>
    //   {value || placeholder || "click to edit"}
    // </span>
    <span
  tabIndex={0}
  role="button"
  onClick={() => setEditing(true)}
  onFocus={() => setEditing(true)}
  onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setEditing(true); } }}
  className={`cursor-text border-b border-dashed ...`}>
  {value || placeholder || "click to edit"}
</span>
  );
}

// ─── Add Modal ───────────────────────────────────────────────────────────────
function AddModal({ title, fields, onClose, onSave }: {
  title: string; fields: { key: string; label: string; required?: boolean; type?: string }[];
  onClose: () => void; onSave: (data: Record<string, string>) => Promise<void>;
}) {
  const [form, setForm] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const handleSave = async () => {
    setSaving(true);
    try { await onSave(form); onClose(); } catch { } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl border-2 border-blue-400 w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-blue-100">
          <h3 className="font-black text-blue-900 text-base">Add {title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>
        <div className="px-6 py-4 space-y-3">
          {fields.map(f => (
            <div key={f.key}>
              <label className="text-xs font-bold text-gray-600 block mb-1">{f.label}{f.required && <span className="text-red-500 ml-0.5">*</span>}</label>
              <input type={f.type || "text"} className="w-full border-2 border-blue-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500 transition-colors"
                value={form[f.key] || ""} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))} placeholder={f.label} />
            </div>
          ))}
        </div>
        <div className="flex gap-3 px-6 pb-5">
          <button onClick={onClose} className="flex-1 py-2.5 border-2 border-gray-200 rounded-xl text-sm font-bold text-gray-600 hover:border-gray-400 transition-colors">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="flex-1 py-2.5 bg-blue-700 hover:bg-blue-800 text-white rounded-xl text-sm font-bold transition-colors disabled:opacity-50">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Note System ─────────────────────────────────────────────────────────────
const NOTE_COLORS = [
  { bg: "bg-yellow-100 border-yellow-400", dot: "bg-yellow-400", label: "Yellow" },
  { bg: "bg-blue-100 border-blue-400", dot: "bg-blue-400", label: "Blue" },
  { bg: "bg-red-100 border-red-400", dot: "bg-red-400", label: "Red" },
  { bg: "bg-green-100 border-green-400", dot: "bg-green-400", label: "Green" },
];

function NotesPanel({ notes, setNotes }: { notes: Note[]; setNotes: React.Dispatch<React.SetStateAction<Note[]>> }) {
  const [adding, setAdding] = useState(false);
  const [newText, setNewText] = useState("");
  const [newColor, setNewColor] = useState(0);
  return (
    <div className="border-t-2 border-dashed border-amber-300 bg-amber-50/60">
      <div className="flex items-center justify-between px-3 py-1.5">
        <span className="text-[9px] font-black uppercase tracking-widest text-amber-700">📌 Notes</span>
        <button type="button" onClick={() => setAdding(v => !v)}
          className="text-[9px] font-bold text-amber-700 hover:text-amber-900 border border-amber-400 rounded px-2 py-0.5 transition">
          {adding ? "Cancel" : "+ Add Note"}
        </button>
      </div>
      {adding && (
        <div className="px-3 pb-2 flex flex-col gap-1.5">
          <textarea className="w-full text-[10px] border border-amber-300 rounded p-1.5 resize-none bg-white focus:outline-none focus:border-amber-500 h-16"
            placeholder="Type your note here…" value={newText} onChange={e => setNewText(e.target.value)} />
          <div className="flex items-center gap-2">
            <div className="flex gap-1">
              {NOTE_COLORS.map((c, i) => (
                <button key={i} type="button" onClick={() => setNewColor(i)}
                  className={`w-4 h-4 rounded-full border-2 ${c.dot} ${newColor === i ? "border-gray-700 scale-125" : "border-transparent"} transition-all`} />
              ))}
            </div>
            <button type="button" disabled={!newText.trim()}
              onClick={() => {
                if (!newText.trim()) return;
                setNotes(p => [...p, { id: Date.now().toString(), text: newText.trim(), color: NOTE_COLORS[newColor].bg }]);
                setNewText(""); setAdding(false);
              }}
              className="ml-auto text-[9px] font-bold bg-amber-500 text-white rounded px-2 py-0.5 disabled:opacity-50 transition">Save Note</button>
          </div>
        </div>
      )}
      {notes.length > 0 && (
        <div className="px-3 pb-2 flex flex-col gap-1">
          {notes.map(n => (
            <div key={n.id} className={`flex items-start gap-1 border rounded p-1.5 ${n.color}`}>
              <p className="text-[9px] flex-1 leading-tight">{n.text}</p>
              <button type="button" onClick={() => setNotes(p => p.filter(x => x.id !== n.id))}
                className="text-[8px] text-red-500 hover:text-red-700 font-black ml-1 shrink-0">✕</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── PDF Styles ──────────────────────────────────────────────────────────────
const S = StyleSheet.create({
  page: { fontFamily: 'Helvetica', fontSize: 9, color: '#000', backgroundColor: '#fff', padding: '10px' },
  outerBorder: { border: '2px solid black', flex: 1 },
  header: { borderBottom: '2px solid black', paddingVertical: 6, paddingHorizontal: 8, alignItems: 'center', backgroundColor: '#f0f0f0' },
  companyName: { fontSize: 22, fontFamily: 'Helvetica-Bold', letterSpacing: 2, textTransform: 'uppercase' },
  companyTagline: { fontSize: 7, color: '#555', marginTop: 1, letterSpacing: 1 },
  companyAddress: { fontSize: 7.5, marginTop: 2, color: '#333' },
  docTitle: { fontSize: 10, fontFamily: 'Helvetica-Bold', letterSpacing: 3, textTransform: 'uppercase', marginTop: 3, borderTop: '1px solid #ccc', paddingTop: 2 },
  gstRow: { flexDirection: 'row', borderBottom: '1px solid black' },
  gstCell: { flex: 1, paddingHorizontal: 6, paddingVertical: 3, borderRight: '1px solid black' },
  gstLabel: { fontSize: 6.5, color: '#888', textTransform: 'uppercase', marginBottom: 1 },
  gstValue: { fontSize: 8.5, fontFamily: 'Helvetica-Bold' },
  ownersRisk: { paddingHorizontal: 6, paddingVertical: 3, minWidth: 100, alignItems: 'flex-end', justifyContent: 'center' },
  ownersRiskText: { fontSize: 7, color: '#c00', fontFamily: 'Helvetica-Bold' },
  emailRow: { borderBottom: '1px solid black', paddingHorizontal: 6, paddingVertical: 2, flexDirection: 'row', justifyContent: 'space-between' },
  banner: { backgroundColor: '#1a1a2e', borderBottom: '1px solid black', paddingVertical: 3, alignItems: 'center' },
  bannerText: { fontSize: 7, letterSpacing: 2, textTransform: 'uppercase', color: '#fff' },
  lrNumberBox: { backgroundColor: '#e8f4ff', borderBottom: '2px solid black', paddingVertical: 4, paddingHorizontal: 8, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  lrNumberLabel: { fontSize: 8, fontFamily: 'Helvetica-Bold', color: '#333' },
  lrNumberValue: { fontSize: 14, fontFamily: 'Helvetica-Bold', color: '#003399', letterSpacing: 1 },
  consignBlock: { flexDirection: 'row', borderBottom: '1px solid black' },
  consignLeft: { flex: 1, paddingHorizontal: 6, paddingVertical: 5, borderRight: '1px solid black' },
  consignLabel: { fontSize: 6.5, color: '#888', textTransform: 'uppercase', marginBottom: 2, letterSpacing: 0.5 },
  consignName: { fontSize: 11, fontFamily: 'Helvetica-Bold' },
  consignGst: { fontSize: 7, color: '#555', marginTop: 1 },
  consignAddress: { fontSize: 7.5, color: '#555', marginTop: 2 },
  consignRight: { width: 185, paddingHorizontal: 6, paddingVertical: 5, backgroundColor: '#fafafa' },
  detailRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 3, borderBottom: '1px dotted #ddd', paddingBottom: 2 },
  detailLabel: { fontFamily: 'Helvetica-Bold', fontSize: 7.5, marginRight: 3, color: '#444', width: 60 },
  detailValue: { fontSize: 7.5, color: '#111', flex: 1 },
  noteBox: { width: 185, paddingHorizontal: 8, paddingVertical: 5, justifyContent: 'center', alignItems: 'center', backgroundColor: '#fff8f0' },
  noteText: { color: '#cc0000', fontFamily: 'Helvetica-Bold', fontSize: 9, textAlign: 'center', lineHeight: 1.4 },
  table: { width: '100%' },
  tableHeaderRow: { flexDirection: 'row', backgroundColor: '#1a1a2e' },
  tableRow: { flexDirection: 'row' },
  tableAltRow: { flexDirection: 'row', backgroundColor: '#f9f9f9' },
  th: { border: '1px solid #333', paddingHorizontal: 4, paddingVertical: 3, fontFamily: 'Helvetica-Bold', fontSize: 7.5, textAlign: 'center', color: '#fff' },
  td: { border: '1px solid #ccc', paddingHorizontal: 4, paddingVertical: 3, fontSize: 7.5 },
  totalsRow: { flexDirection: 'row', borderTop: '2px solid black' },
  totalsLeft: { flex: 1, borderRight: '1px solid black', paddingHorizontal: 6, paddingVertical: 5 },
  totalsRight: { width: 185, backgroundColor: '#fafafa' },
  totalLineRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingHorizontal: 6, paddingVertical: 2, borderBottom: '1px solid #e0e0e0' },
  totalLineBold: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingHorizontal: 6, paddingVertical: 4, borderBottom: '2px solid black', backgroundColor: '#1a1a2e' },
  totalLineLight: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingHorizontal: 6, paddingVertical: 2, borderBottom: '1px solid #eee' },
  totalLabel: { fontSize: 7.5 },
  totalValue: { fontSize: 7.5, fontFamily: 'Helvetica-Bold' },
  footerRow: { flexDirection: 'row', borderTop: '1px solid black' },
  footerLeft: { flex: 1, borderRight: '1px solid black', paddingHorizontal: 6, paddingVertical: 5 },
  footerRight: { width: 185, paddingHorizontal: 8, paddingVertical: 5, alignItems: 'center' },
  paymentRow: { flexDirection: 'row', borderTop: '1px solid black', paddingHorizontal: 6, paddingVertical: 3, alignItems: 'center', backgroundColor: '#f5f5f5' },
  amountWordsRow: { borderTop: '1px solid black', paddingHorizontal: 6, paddingVertical: 3 },
  computerNote: { borderTop: '1px solid black', paddingVertical: 3, alignItems: 'center', backgroundColor: '#f0f0f0' },
  bold: { fontFamily: 'Helvetica-Bold' },
  gray: { color: '#666' },
  small: { fontSize: 7 },
  white: { color: '#fff' },
});

// ─── PDF Document ─────────────────────────────────────────────────────────────
function InvoicePDF({ company, sender, senders = [], receiver, selectedVehicle, origin, destination, items, paymentType, invoiceId, selectedDate, subject, notes, driverInput, partyInvoiceNo, invoiceValue, deliveryAt, ewayBillNo,selectedBank,Round_off }: any) {
  // const safeItems = Array.isArray(items) ? items.filter((i: any) => i?.name) : [];
  const safeItems = Array.isArray(items) ? items.filter((i: any) => 
    i?.name || i?.hsnCode || resolveItemAmount(i) > 0 || parseFloat(i?.qty) > 0
  ) : [];
  const total = safeItems.reduce((sum: number, item: any) => sum + resolveItemAmount(item), 0);
  const grandTotal = items.reduce((acc: number, it: Item) => acc + resolveItemAmount(it), 0);
const roundOffNum = parseFloat(Round_off) || 0;
const grandTotalWithRoundOff = grandTotal + roundOffNum;
  const paymentLabel = paymentType === 'paid' ? 'PAID' : paymentType === 'to_pay' ? 'TO PAY' : 'TO BE BILLED';
  const colFlex = [0.35, 2.8, 0.9, 0.7, 0.8, 0.9, 1];
  const colHeaders = ['#', 'Product Description', 'HSN Code', 'UOM', 'Qty', 'Rate/Ton', 'Amount'];
  const displayItems = safeItems.length > 0 ? safeItems : [{}, {}, {}, {}];
  const vehicleTypeLabel = selectedVehicle?.vehicle_type ? selectedVehicle.vehicle_type.toUpperCase() : 'Vehicle';

  const activeSenders: any[] = senders && senders.length > 0 ? senders : (sender ? [sender] : []);
  const primarySender = activeSenders[0] || null;

  const bankName = selectedBank?.bank_name || primarySender?.bank_name || '—';
  const bankAc = selectedBank?.account_number || primarySender?.bank_account_number || '—';
  const bankIfsc = selectedBank?.ifsc_code || primarySender?.bank_ifsc || '—';
  const bankHolder = selectedBank?.account_holder_name || primarySender?.account_holder_name || '';
  const bankBranch = selectedBank?.branch || '';
  return (
    <Document>
      <Page size="A4" style={S.page}>
        <View style={S.outerBorder}>
          <View style={S.header}>
            <Text style={S.companyName}>{company?.company_name || 'COMPANY NAME'}</Text>
            <Text style={S.companyTagline}>GOODS TRANSPORT AGENCY</Text>
            <Text style={S.companyAddress}>{company?.address || ''}</Text>
            <Text style={S.docTitle}>LORRY RECEIPT (LR)</Text>
          </View>
          <View style={S.gstRow}>
            <View style={S.gstCell}><Text style={S.gstLabel}>GST Enrol No:</Text><Text style={S.gstValue}>{company?.gst_number || '—'}</Text></View>
            <View style={S.gstCell}><Text style={S.gstLabel}>PAN No.</Text><Text style={S.gstValue}>{company?.pancard || '—'}</Text></View>
            <View style={S.gstCell}><Text style={S.gstLabel}>CIN No.</Text><Text style={S.gstValue}>{company?.cin || '—'}</Text></View>
            <View style={S.ownersRisk}><Text style={S.ownersRiskText}>AT OWNER'S RISK</Text></View>
          </View>
          <View style={S.emailRow}>
            <Text style={{ fontSize: 7.5 }}>Email: {company?.email || ''}</Text>
            <Text style={{ fontSize: 7.5 }}>Subject to {subject || 'Kishangarh'} Jurisdiction</Text>
          </View>
          <View style={S.lrNumberBox}>
            <Text style={S.lrNumberLabel}>LR / GR No.</Text>
            <Text style={S.lrNumberValue}>{invoiceId || '—'}</Text>
            <Text style={S.lrNumberLabel}>Date: {fmtDate(selectedDate)}</Text>
          </View>
          <View style={S.banner}><Text style={S.bannerText}>LORRY RECEIPT / CONSIGNMENT NOTE</Text></View>
          <View style={S.consignBlock}>
            <View style={S.consignLeft}>
              <Text style={S.consignLabel}>
                Consignor / Supplier Name & Address{activeSenders.length > 1 ? ` (${activeSenders.length})` : ''}
              </Text>
              {activeSenders.length > 0 ? (
                activeSenders.map((s: any, idx: number) => (
                  <View key={s?.id || idx} style={idx > 0 ? { marginTop: 3, paddingTop: 2, borderTopWidth: 0.5, borderTopColor: '#d1d5db' } : {}}>
                    <Text style={S.consignName}>{activeSenders.length > 1 ? `${idx + 1}. ` : ''}{s?.name || '—'}</Text>
                    {s?.gst_number && <Text style={S.consignGst}>GST: {s.gst_number}</Text>}
                    <Text style={S.consignAddress}>{s?.address || ''}</Text>
                    {s?.number && <Text style={[S.consignAddress, { marginTop: 1 }]}>Ph: {s.number}</Text>}
                  </View>
                ))
              ) : (
                <Text style={S.consignName}>—</Text>
              )}
            </View>
            <View style={S.consignRight}>
              {/* vehicle type as label, vehicle number as value */}
              <View style={S.detailRow}><Text style={S.detailLabel}>{vehicleTypeLabel}:</Text><Text style={S.detailValue}>{selectedVehicle?.vehicle_number || '—'}</Text></View>
              <View style={S.detailRow}><Text style={S.detailLabel}>FROM:</Text><Text style={S.detailValue}>{origin || '—'}</Text></View>
              <View style={S.detailRow}><Text style={S.detailLabel}>TO:</Text><Text style={S.detailValue}>{destination || '—'}</Text></View>
            </View>
          </View>
          <View style={S.consignBlock}>
            <View style={S.consignLeft}>
              <Text style={S.consignLabel}>Consignee / Recipient Name & Address</Text>
              <Text style={S.consignName}>{receiver?.name || '—'}</Text>
              {receiver?.gst_number && <Text style={S.consignGst}>GST: {receiver.gst_number}</Text>}
              <Text style={S.consignAddress}>{receiver?.address || ''}</Text>
              {receiver?.number && <Text style={[S.consignAddress, { marginTop: 2 }]}>Ph: {receiver.number}</Text>}
            </View>
            <View style={S.noteBox}>
              <Text style={S.noteText}>NOTE —{'\n'}NO CASH{'\n'}ONLY CHEQUE OR POD</Text>
            </View>
          </View>
          <View style={S.table}>
            <View style={S.tableHeaderRow}>
              {colHeaders.map((h, i) => <View key={h} style={[S.th, { flex: colFlex[i] }]}><Text>{h}</Text></View>)}
            </View>
            {displayItems.map((item: any, i: number) => {
  const resolvedAmt = resolveItemAmount(item);
  const qty = parseFloat(item.qty) || 0;
  const rate = parseFloat(item.ratePerQtl) || 0;
  const displayRate = rate > 0 ? rate : 0;
  const hasData = Boolean(item?.name || item?.hsnCode || resolvedAmt > 0 || qty > 0);
  return (
    <View key={i} style={i % 2 === 0 ? S.tableRow : S.tableAltRow}>
      <View style={[S.td, { flex: colFlex[0], textAlign: 'center' }]}>
        <Text>{hasData ? String(i + 1) : ''}</Text>
      </View>
      <View style={[S.td, { flex: colFlex[1] }]}>
        <Text>{item.name || ''}</Text>
      </View>
      <View style={[S.td, { flex: colFlex[2], textAlign: 'center' }]}>
        <Text>{hasData ? (item.hsnCode || company?.HSN_SAC || '—') : ''}</Text>
      </View>
      <View style={[S.td, { flex: colFlex[3], textAlign: 'center' }]}>
        <Text>{hasData ? (item.uom || 'TON').toUpperCase() : ''}</Text>
      </View>
      <View style={[S.td, { flex: colFlex[4], textAlign: 'right' }]}>
        <Text>{item.qty || ''}</Text>
      </View>
      <View style={[S.td, { flex: colFlex[5], textAlign: 'right' }]}>
        <Text>{displayRate > 0 ? `Rs. ${displayRate.toLocaleString('en-IN')} /-` : ''}</Text>
      </View>
      <View style={[S.td, { flex: colFlex[6], textAlign: 'right' }]}>
        <Text>{resolvedAmt > 0 ? `Rs. ${resolvedAmt.toLocaleString('en-IN')} /-` : ''}</Text>
      </View>
    </View>
  );
})}
          </View>
          <View style={S.totalsRow}>
            <View style={S.totalsLeft}>
            <Text style={[S.bold, { fontSize: 8, marginBottom: 3 }]}>GST WILL BE PAID BY CONSIGNEE</Text>
              <Text style={[S.bold, { fontSize: 7.5, marginBottom: 2 }]}>BANK: {bankName}</Text>
              {bankHolder ? <Text style={{ fontSize: 7 }}>A/C Name: {bankHolder}</Text> : null}
              <Text style={{ fontSize: 7.5 }}>A/C: {bankAc} | IFSC: {bankIfsc}</Text>
              {bankBranch ? <Text style={{ fontSize: 7, color: '#555' }}>Branch: {bankBranch}</Text> : null}
            </View>
            <View style={S.totalsRight}>
              <View style={S.totalLineRow}><Text style={S.totalLabel}>SUB TOTAL</Text><Text style={S.totalValue}>{total > 0 ? `Rs. ${total.toLocaleString('en-IN')} /-` : ''}</Text></View>
              <View style={S.totalLineRow}><Text style={S.totalLabel}>ROUND OFF</Text><Text style={S.totalValue}>{Round_off}</Text></View>
              <View style={S.totalLineBold}>
                <Text style={[S.totalLabel, S.bold, S.white]}>GRAND TOTAL</Text>
                <Text style={[S.totalValue, S.bold, S.white]}>{grandTotalWithRoundOff > 0 ? `Rs. ${grandTotalWithRoundOff.toLocaleString('en-IN')} /-` : ''}</Text>
              </View>
              <View style={S.totalLineLight}><Text style={S.totalLabel}>PARTY INVOICE NO.:</Text><Text style={S.totalValue}>{partyInvoiceNo  || ''}</Text></View>
              <View style={S.totalLineLight}><Text style={S.totalLabel}>INVOICE VALUE:</Text><Text style={S.totalValue}>{invoiceValue || ''}</Text></View>
              <View style={S.totalLineLight}><Text style={S.totalLabel}>DELIVERY AT:</Text><Text style={S.totalValue}>{deliveryAt || destination || ''}</Text></View>
              <View style={S.totalLineLight}><Text style={S.totalLabel}>EWAY BILL NO.:</Text><Text style={S.totalValue}>{ewayBillNo || ''}</Text></View>
            </View>
          </View>
          <View style={S.footerRow}>
            <View style={S.footerLeft}>
              <Text style={[S.bold, { fontSize: 7.5, marginBottom: 2 }]}>TERMS & CONDITIONS:</Text>
              <Text style={[S.gray, { fontSize: 7 }]}>1. Carrier not responsible for leakage, breakage, or water damage.</Text>
              <Text style={[S.gray, { fontSize: 7 }]}>2. Goods booked at owner's risk unless otherwise stated.</Text>
              <Text style={[S.gray, { fontSize: 7 }]}>3. Claims must be notified within 24 hours of delivery.</Text>
              <Text style={[S.bold, { fontSize: 7.5, marginTop: 4 }]}>E. & O.E.</Text>
            </View>
            <View style={S.footerRight}>
              {company?.signature ? <Image src={`${process.env.REACT_APP_API_URL}${company.signature}`} style={{ maxHeight: 36, objectFit: 'contain', marginBottom: 4 }} /> : <View style={{ height: 36 }} />}
              <Text style={[S.bold, { fontSize: 7.5, textAlign: 'center' }]}>For {company?.company_name || 'COMPANY'}</Text>
              <Text style={[{ fontSize: 7, textAlign: 'center', marginTop: 2, color: '#555' }]}>Authorised Signatory</Text>
            </View>
          </View>
          <View style={{ flexDirection: 'row', borderTop: '1px solid black' }}>
            <View style={{ flex: 1, borderRight: '1px solid black', paddingHorizontal: 6, paddingVertical: 4 }}>
              <Text style={[S.bold, { fontSize: 7.5, marginBottom: 3, borderBottom: '1px solid #ddd', paddingBottom: 2 }]}>DRIVER DETAILS</Text>
              <View style={S.detailRow}><Text style={S.detailLabel}>NAME:</Text><Text style={S.detailValue}>{driverInput?.name || '—'}</Text></View>
              <View style={S.detailRow}><Text style={S.detailLabel}>PHONE:</Text><Text style={S.detailValue}>{driverInput?.phone || '—'}</Text></View>
              {driverInput?.license_number && (
                <View style={S.detailRow}><Text style={S.detailLabel}>LICENSE:</Text><Text style={S.detailValue}>{driverInput.license_number}</Text></View>
              )}
              <View style={{ marginTop: 8 }}>
                <Text style={{ fontSize: 7, color: '#888' }}>Driver Signature:</Text>
                <View style={{ borderBottom: '1px solid #333', marginTop: 14, marginHorizontal: 4 }} />
              </View>
            </View>
            <View style={{ flex: 1, paddingHorizontal: 6, paddingVertical: 4, backgroundColor: '#fffdf0' }}>
              <Text style={[S.bold, { fontSize: 7.5, marginBottom: 3, borderBottom: '1px solid #ddd', paddingBottom: 2 }]}>REMARKS / NOTES</Text>
              {notes && notes.length > 0
                ? notes.map((n: any, i: number) => (<Text key={i} style={{ fontSize: 7, color: '#333', marginBottom: 2 }}>• {n.text}</Text>))
                : <Text style={{ fontSize: 7, color: '#bbb', fontStyle: 'italic' }}>No remarks</Text>
              }
            </View>
          </View>
          <View style={S.paymentRow}>
            <Text style={[S.bold, { fontSize: 8 }]}>PAYMENT MODE: </Text>
            {['PAID', 'TO PAY', 'TO BE BILLED'].map(pt => (
              <Text key={pt} style={[S.bold, { fontSize: 8, marginLeft: 6, paddingHorizontal: 8, paddingVertical: 2, backgroundColor: paymentLabel === pt ? '#1a1a2e' : '#fff', color: paymentLabel === pt ? '#fff' : '#000', border: '1px solid #333' }]}>{pt}</Text>
            ))}
          </View>
          <View style={S.amountWordsRow}>
            <Text style={{ fontSize: 7.5 }}>
              <Text style={S.bold}>AMOUNT IN WORDS: </Text>
              <Text style={[S.gray, { fontStyle: 'italic' }]}>{grandTotalWithRoundOff > 0 ? numberToWords(grandTotalWithRoundOff) : ''}</Text>
            </Text>
          </View>
          <View style={S.computerNote}><Text style={[S.gray, S.small]}>THIS IS A COMPUTER GENERATED LR AND DOES NOT REQUIRE A SIGNATURE.</Text></View>
        </View>
      </Page>
    </Document>
  );
}

// ─── Invoice Preview ──────────────────────────────────────────────────────────
function InvoicePreview({
  company, sender, senders = [], setSenders, receiver, vehicle, driverInput, setDriverInput,
  origin, destination, items, paymentType, paymentPaidBy, invoiceNumber, selectedDate, subject,
  notes, setNotes, setCompany, setModal, setSender, users, vehicles, setVehicle,
  setReceiver, updateItem, setItems, setInvoiceNumber, setOrigin, setDestination,
  setSubject, setPaymentType, setPaymentPaidBy, companies,
  partyInvoiceNo, setPartyInvoiceNo,
  invoiceValue, setInvoiceValue,
  deliveryAt, setDeliveryAt,
  ewayBillNo, setEwayBillNo,
  companyBanks, selectedBank, setSelectedBank,excludeInvoiceDbId,
  roundOff, setRoundOff,
  transporter, hasBroker, brokerPaidBy, description
}: any) {

  // const grandTotal = items.reduce((acc: number, it: Item) => acc + resolveItemAmount(it), 0);
  const grandTotal = items.reduce((acc: number, it: Item) => acc + resolveItemAmount(it), 0);
const roundOffNum = parseFloat(roundOff) || 0;
const grandTotalWithRoundOff = grandTotal + roundOffNum;

  const displayLrNo = invoiceNumber.trim() || (origin && destination && vehicle
    ? generateInvoiceId(origin, destination, vehicle.vehicle_number, selectedDate)
    : "LR-AUTO");

  const sendersList: any[] = senders && senders.length > 0 ? senders : (sender ? [sender] : []);
  const availableConsignors = users.filter((u: any) => u.role === 'consignor' || !u.role);
  const consignorDropdownOptions = availableConsignors.length > 0 ? availableConsignors : users;

  return (
    <div className="bg-white border-2 border-gray-900 shadow-2xl font-sans text-[10px] rounded-sm overflow-hidden w-full max-w-full sm:max-w-[500px] md:max-w-[650px] lg:max-w-[75vw] mx-auto" style={{ boxSizing: "border-box" }}>

      {/* Company Header */}
      <div className="text-center bg-gray-100 border-b-2 border-black py-3 px-2 sm:px-4">
        <div className="text-[8px] text-blue-500 font-bold uppercase tracking-widest mb-1 opacity-60">↓ Click to change company <span className="text-red-500">*required</span></div>
        <FieldDropdown<any>
          label="Company" value={company} options={companies}
          getLabel={(c: any) => c.company_name}
          getSub={(c: any) => `${c.city || ""} · GST: ${c.gst_number || ""}`}
          onSelect={setCompany} onAdd={() => setModal("company")} placeholder="SELECT COMPANY"
          className="mx-auto"
        />
        {company && <>
          <p className="text-[7px] uppercase tracking-widest text-gray-500 mt-0.5">Goods Transport Agency</p>
          <p className="text-[8px] mt-0.5 text-gray-600">{company.address}</p>
          <div className="mt-1 text-[8px] font-black tracking-[3px] border-t border-gray-300 pt-1 text-gray-700">LORRY RECEIPT (LR)</div>
        </>}
      </div>

      {/* GST / PAN / CIN */}
      <div className="flex flex-col sm:flex-row border-b border-black text-[9px]">
        <div className="flex-1 px-2 py-1.5 border-b sm:border-b-0 sm:border-r border-black">
          <span className="text-[7px] text-gray-400 uppercase block">GST Enrol No.</span>
          <span className="font-bold">{company?.gst_number || <span className="text-gray-300">—</span>}</span>
        </div>
        <div className="flex-1 px-2 py-1.5 border-b sm:border-b-0 sm:border-r border-black">
          <span className="text-[7px] text-gray-400 uppercase block">PAN No.</span>
          <span className="font-bold">{company?.pancard || <span className="text-gray-300">—</span>}</span>
        </div>
        <div className="flex-1 px-2 py-1.5 border-b sm:border-b-0 sm:border-r border-black">
          <span className="text-[7px] text-gray-400 uppercase block">CIN No.</span>
          <span className="font-bold">{company?.cin || <span className="text-gray-300">—</span>}</span>
        </div>
        <div className="px-2 py-1.5 flex items-center justify-end min-w-[90px]">
          <span className="text-[7px] text-red-600 font-bold">AT OWNER'S RISK</span>
        </div>
      </div>

      {/* Email / Subject */}
      <div className="px-2 py-1 text-[8px] border-b border-black flex flex-col sm:flex-row justify-between sm:items-center gap-1">
        <span>Email: {company?.email || <span className="text-gray-300">—</span>}</span>
        <span className="italic text-gray-500">
          Subject to&nbsp;
          <InlineEdit value={subject} onChange={setSubject} placeholder="Kishangarh" className="text-[8px]" small />
          &nbsp;Jurisdiction
        </span>
      </div>

      {/* LR Number Banner */}
      <div className="bg-blue-50 border-b-2 border-black px-2 sm:px-3 py-1.5 flex flex-col sm:flex-row justify-between items-center gap-y-2">
        <LrNumberInput
          invoiceNumber={invoiceNumber}
          setInvoiceNumber={setInvoiceNumber}
          displayLrNo={displayLrNo}
          companyId={company?.id ?? null}
          excludeInvoiceDbId={excludeInvoiceDbId}
        />
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => exportConsignmentToExcel({
            company, sender, senders: sendersList, receiver, vehicle, driverInput, transporter,
            invoiceNumber: displayLrNo, selectedDate, origin, destination, subject, items,
            paymentType, paymentPaidBy, selectedBank, partyInvoiceNo, invoiceValue,
            deliveryAt, ewayBillNo, roundOff, hasBroker, brokerPaidBy, notes, description
          })} className="px-2.5 py-1 bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-[9px] rounded shadow-sm transition-colors flex items-center gap-1">
            📊 Download Excel
          </button>
          <div className="text-left sm:text-right">
            <span className="text-[7px] text-gray-500 uppercase block">Date</span>
            <span className="font-bold text-gray-900">
              {(() => {
                const d = new Date(selectedDate);
                if (isNaN(d.getTime())) return "";
                const mm = String(d.getMonth()+1).padStart(2, "0");
                const dd = String(d.getDate()).padStart(2, "0");
                const yy = String(d.getFullYear()).slice(-2);
                return `${mm}/${dd}/${yy}`;
              })()}
            </span>
          </div>
        </div>
      </div>

      {/* Dark Banner */}
      <div className="bg-[#1a1a2e] text-center py-1.5 border-b border-black">
        <span className="text-[7px] tracking-[3px] uppercase text-white font-bold">LORRY RECEIPT / CONSIGNMENT NOTE</span>
      </div>

      {/* Consignor */}
      <div className="flex flex-col md:flex-row border-b border-black">
        <div className="flex-1 px-2 py-2 border-b md:border-b-0 md:border-r border-black">
          <div className="flex items-center justify-between mb-1">
            <div className="text-[7px] text-gray-400 uppercase tracking-wide">
              Consignor / Supplier Name & Address <span className="text-gray-300 normal-case">(optional)</span>
            </div>
            {sendersList.length > 1 && (
              <span className="text-[8px] font-black bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">
                {sendersList.length} Consignors
              </span>
            )}
          </div>

          {sendersList.length === 0 ? (
            <FieldDropdown<any>
              label="Sender"
              value={null}
              options={consignorDropdownOptions}
              getLabel={(u: any) => u.name}
              getSub={(u: any) => `${u.number}${u.gst_number ? ` · GST: ${u.gst_number}` : ""} · ${u.address || ""}`}
              onSelect={(u: any) => {
                if (setSenders) setSenders([u]);
                else setSender(u);
              }}
              onAdd={() => setModal("sender")}
              placeholder="Select Sender (optional)"
            />
          ) : (
            <div className="space-y-2">
              {sendersList.map((s: any, idx: number) => (
                <div key={s.id || idx} className={`${idx > 0 ? "pt-2 border-t border-dashed border-gray-300" : ""}`}>
                  <div className="flex items-center justify-between gap-1">
                    <span className="font-black text-black text-xs leading-tight">
                      {sendersList.length > 1 && <span className="text-blue-600 font-bold mr-1">#{idx + 1}</span>}
                      {s.name}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        if (setSenders) {
                          setSenders((prev: any[]) => prev.filter((x: any) => x.id !== s.id));
                        } else {
                          setSender(null);
                        }
                      }}
                      className="text-gray-400 hover:text-red-600 text-xs font-bold px-1 rounded transition-colors"
                      title="Remove this consignor"
                    >
                      ✕
                    </button>
                  </div>
                  <div className="text-[8px] text-gray-500 mt-0.5 flex items-center gap-1">
                    <span>GST:</span>
                    <InlineEdit
                      value={s.gst_number || ""}
                      onChange={(val) => {
                        if (setSenders) {
                          setSenders((prev: any[]) => prev.map((x: any) => x.id === s.id ? { ...x, gst_number: val } : x));
                        } else {
                          setSender((prev: any) => prev ? { ...prev, gst_number: val } : null);
                        }
                      }}
                      placeholder="GST Number"
                      small
                    />
                  </div>
                  <div className="text-[8px] text-gray-500 mt-0.5">{s.address}</div>
                  <div className="text-[8px] text-gray-500">Ph: {s.number}</div>
                </div>
              ))}

              <div className="pt-1.5 flex items-center gap-2 border-t border-dotted border-blue-200">
                <FieldDropdown<any>
                  label="Sender"
                  value={null}
                  options={consignorDropdownOptions.filter((u: any) => !sendersList.some((sel: any) => sel.id === u.id))}
                  getLabel={(u: any) => u.name}
                  getSub={(u: any) => `${u.number}${u.gst_number ? ` · GST: ${u.gst_number}` : ""} · ${u.address || ""}`}
                  onSelect={(u: any) => {
                    if (setSenders) {
                      setSenders((prev: any[]) => [...prev, u]);
                    } else {
                      setSender(u);
                    }
                  }}
                  onAdd={() => setModal("sender")}
                  placeholder="+ Add another Consignor"
                  className="text-[9px]"
                />
              </div>
            </div>
          )}
        </div>
        <div className="w-full md:w-52 px-2 py-2 text-[9px] space-y-1 bg-gray-50">
          {[
            {
              label: vehicle?.vehicle_type ? vehicle.vehicle_type.toUpperCase() + ":" : "VEHICLE:",
              content: <FieldDropdown<any> label="Vehicle" value={vehicle} options={vehicles} getLabel={(v: any) => v.vehicle_number || v.registration_number} getSub={(v: any) => v.vehicle_type || ""} onSelect={setVehicle} onAdd={() => setModal("vehicle")} placeholder="Select (optional)" />
            },
            { label: "FROM:", content: <InlineEdit value={origin} onChange={setOrigin} placeholder="origin city" small /> },
            { label: "TO:", content: <InlineEdit value={destination} onChange={setDestination} placeholder="destination city" small /> },
          ].map(({ label, content }) => (
            <div key={label} className="flex items-center gap-1 border-b border-dotted border-gray-200 pb-1">
              <span className="font-bold text-gray-600 w-20 shrink-0 text-[8px]">{label}</span>
              {content}
            </div>
          ))}
        </div>
      </div>

      {/* Consignee */}
      <div className="flex flex-col md:flex-row border-b border-black">
        <div className="flex-1 px-2 py-2 border-b md:border-b-0 md:border-r border-black">
          <div className="text-[7px] text-gray-400 uppercase tracking-wide mb-1">Consignee / Recipient Name & Address</div>
          <FieldDropdown<any> label="Receiver" value={receiver} options={users}
            getLabel={(u: any) => u.name} getSub={(u: any) => `${u.number}${u.gst_number ? ` · GST: ${u.gst_number}` : ""} · ${u.address || ""}`}
            onSelect={setReceiver} onAdd={() => setModal("receiver")} placeholder="Select Receiver (optional)" />
          {receiver && <>
            <div className="text-[8px] text-gray-500 mt-0.5 flex items-center gap-1">
              <span>GST:</span>
              <InlineEdit value={receiver.gst_number || ""} onChange={(val) => setReceiver((prev: any) => prev ? { ...prev, gst_number: val } : null)} placeholder="GST Number" small />
            </div>
            <div className="text-[8px] text-gray-500 mt-0.5">{receiver.address}</div>
            <div className="text-[8px] text-gray-500">Ph: {receiver.number}</div>
          </>}
        </div>
        <div className="w-full md:w-52 flex items-center justify-center px-2 sm:px-4 py-2 bg-orange-50">
          <p className="text-red-700 font-black text-[10px] text-center leading-snug border border-red-300 rounded p-2 bg-white">NOTE —<br />NO CASH<br />ONLY CHEQUE OR POD</p>
        </div>
      </div>

      {/* Items Table */}
      <div className="w-full overflow-x-auto">
        <table className="w-[800px] min-w-full sm:w-full border-collapse text-[9px]">
          <thead>
            <tr className="bg-[#1a1a2e] text-white">
              <th className="border border-gray-600 px-1 py-1 w-5 text-center">#</th>
              <th className="border border-gray-600 px-2 py-1 text-left">Product Description</th>
              <th className="border border-gray-600 px-1 py-1 w-12">HSN</th>
              <th className="border border-gray-600 px-1 py-1 w-16">UOM</th>
              <th className="border border-gray-600 px-1 py-1 w-14 text-right">Qty</th>
              <th className="border border-gray-600 px-1 py-1 w-20 text-right">Rate/Unit</th>
              <th className="border border-gray-600 px-1 py-1 w-20 text-right">Amount</th>
              <th className="border border-gray-600 px-1 py-1 w-5"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((item: Item, i: number) => {
              const qty = parseFloat(item.qty) || 0;
              const rate = parseFloat(item.ratePerQtl) || 0;
              const derivedAmt = qty * rate;
              return (
                <tr key={i} className={`group ${i % 2 === 1 ? "bg-gray-50" : ""}`}>
                  <td className="border border-gray-200 px-1 py-1 text-center text-[8px] text-gray-400">{i + 1}</td>
                  <td className="border border-gray-200 px-2 py-1">
                    <input className="w-full bg-transparent outline-none border-b border-dashed border-blue-200 focus:border-blue-500 text-[9px] font-semibold placeholder:text-gray-300"
                      placeholder="Item name…" value={item.name} onChange={e => updateItem(i, "name", e.target.value)} />
                  </td>
                  <td className="border border-gray-200 px-1 py-1 text-center">
                    <input className="w-full bg-transparent outline-none border-b border-dashed border-blue-200 focus:border-blue-500 text-[9px] font-semibold text-center placeholder:text-gray-300"
                      placeholder="—" value={item.hsnCode || ""} onChange={e => updateItem(i, "hsnCode", e.target.value)} />
                  </td>
                  <td className="border border-gray-200 px-1 py-1 text-center">
                    <select
                      value={item.uom || "Ton"}
                      onChange={e => updateItem(i, "uom", e.target.value)}
                      className="w-full bg-transparent outline-none border border-blue-200 rounded text-[8px] font-bold text-center cursor-pointer hover:border-blue-400 focus:border-blue-500 py-0.5 px-0.5"
                    >
                      <option value="Ton">Ton</option>
                      <option value="Box">Box</option>
                    </select>
                  </td>
                  <td className="border border-gray-200 px-1 py-1">
                    <input type="number" className="w-full bg-transparent outline-none border-b border-dashed border-blue-200 focus:border-blue-500 text-[9px] font-semibold text-right placeholder:text-gray-300"
                      placeholder="0" value={item.qty} onChange={e => updateItem(i, "qty", e.target.value)} min={0} />
                  </td>
                  <td className="border border-gray-200 px-1 py-1">
                    <input type="number" className="w-full bg-transparent outline-none border-b border-dashed border-blue-200 focus:border-blue-500 text-[9px] font-semibold text-right placeholder:text-gray-300"
                      placeholder="Rs.0/-" value={item.ratePerQtl}
                      onChange={e => { updateItem(i, "ratePerQtl", e.target.value); updateItem(i, "amount", ""); }} min={0} />
                  </td>
                  <td className="border border-gray-200 px-1 py-1">
                    <input
                      type="number"
                      step="0.01"
                      className="w-full bg-transparent outline-none border-b border-dashed border-blue-200 focus:border-blue-500 text-[9px] font-semibold text-right placeholder:text-gray-300"
                      placeholder={
                        derivedAmt > 0
                          ? `Rs. ${derivedAmt.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} /-`
                          : "Rs.0.00/-"
                      }
                      value={
                        typeof item.amount === "number"
                          ? Number(item.amount).toFixed(2)
                     
                          : item.amount && !isNaN(Number(item.amount))
                          ? Number(item.amount).toFixed(2)
                          : item.amount
                      }
                      onChange={e => {
                        const val = parseFloat(e.target.value);
                        updateItem(i, "amount", isNaN(val) ? "" : val.toFixed(2));
                        updateItem(i, "ratePerQtl", "");
                      }}
                      min={0}
                    />
                  </td>
            
                  <td className="border border-gray-200 px-1 py-1 text-center">
                    {i > 0 && (
                      <button type="button" onClick={() => setItems((p: Item[]) => p.filter((_: Item, j: number) => j !== i))}
                        className="text-red-400 hover:text-red-700 opacity-0 group-hover:opacity-100 transition-opacity text-[10px] font-black">×</button>
                    )}
                  </td>
                </tr>
              );
            })}
            <tr>
              <td colSpan={8} className="border border-gray-200 px-2 py-1">
                <button type="button" onClick={() => setItems((p: Item[]) => [...p, { name: "", hsnCode: "", qty: "", weight: "", ratePerQtl: "", amount: "", uom: "Ton" }])}
                  className="text-blue-600 hover:text-blue-800 text-[8px] font-bold flex items-center gap-1 transition-colors">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                  Add row
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Totals */}
      <div className="flex flex-col md:flex-row border-t-2 border-black">
        <div className="flex-1 px-2 py-2 border-b md:border-b-0 md:border-r border-black text-[8px]">
          <p className="font-bold text-[9px] mb-1">GST WILL BE PAID BY CONSIGNEE</p>
          {/* <p className="font-bold mb-0.5">BANK: {sender?.bank_name || <span className="font-normal text-gray-400">—</span>}</p>
          <p>A/C: {sender?.bank_account_number || "—"} | IFSC: {sender?.bank_ifsc || "—"}</p> */}

 {/* ── BANK DETAILS BLOCK ── */}
{/* ── BANK DETAILS BLOCK ── */}
<div className="border border-blue-100 rounded-md bg-blue-50/60 px-2 py-1.5 mt-1">
  <div className="flex items-center justify-between mb-1">
    <span className="text-[7px] font-black text-blue-800 uppercase tracking-wide">Bank Details</span>
    {companyBanks && companyBanks.length > 1 && (
      <BankSelector banks={companyBanks} selectedBank={selectedBank} onSelect={setSelectedBank} />
    )}
    {companyBanks && companyBanks.length === 1 && selectedBank && (
      <span className="text-[7px] text-green-600 bg-green-50 border border-green-200 rounded px-1">auto-selected</span>
    )}
  </div>

  {selectedBank ? (
    <div className="space-y-0.5">
      <p className="font-bold text-[9px]">{selectedBank.bank_name}</p>
      {selectedBank.account_holder_name && <p className="text-gray-500 text-[8px]">{selectedBank.account_holder_name}</p>}
      <p className="text-[8px]">A/C: <span className="font-bold font-mono">{selectedBank.account_number}</span></p>
      {selectedBank.ifsc_code && <p className="text-[8px]">IFSC: <span className="font-bold">{selectedBank.ifsc_code}</span></p>}
      {selectedBank.branch && <p className="text-gray-500 text-[8px]">Branch: {selectedBank.branch}</p>}
      {selectedBank.account_type && <p className="text-gray-500 text-[8px]">Type: {selectedBank.account_type}</p>}
    </div>
  ) : sender && (sender.bank_name || sender.bank_account_number) ? (
    <div className="space-y-0.5">
      <p className="font-bold text-[9px]">{sender.bank_name || <span className="text-gray-300">—</span>}</p>
      {sender.account_holder_name && <p className="text-gray-500 text-[8px]">{sender.account_holder_name}</p>}
      <p className="text-[8px]">A/C: <span className="font-bold font-mono">{sender.bank_account_number || "—"}</span> | IFSC: <span className="font-bold">{sender.bank_ifsc || "—"}</span></p>
      <p className="text-[7px] text-amber-600 italic">* from sender profile</p>
    </div>
  ) : (
    <p className="text-[8px] text-gray-400 italic">
      {companyBanks && companyBanks.length === 0
        ? "No bank accounts found for this company."
        : "Select a company to load bank details."}
    </p>
  )}
</div>
        </div>
        <div className="w-full md:w-52 text-[9px]">
          {/* {[["SUB TOTAL", grandTotal > 0 ? `Rs. ${grandTotal.toLocaleString("en-IN")} /-` : ""],
            ["ROUND OFF", ""]].map(([l, v]) => (
              <div key={l} className="flex justify-between px-2 py-1 border-b border-gray-100">
                <span className="text-gray-500">{l}</span><span className="font-bold">{v}</span>
              </div>
            ))} */}
            <div className="flex justify-between px-2 py-1 border-b border-gray-100">
  <span className="text-gray-500">SUB TOTAL</span>
  <span className="font-bold">{grandTotal > 0 ? `Rs. ${grandTotal.toLocaleString("en-IN")} /-` : ""}</span>
</div>
<div className="flex justify-between items-center px-2 py-1 border-b border-gray-100">
  <span className="text-gray-500">ROUND OFF</span>
  <input
    type="number"
    step="0.01"
    className="w-20 bg-transparent outline-none border-b border-dashed border-blue-200 focus:border-blue-500 text-[9px] font-semibold text-right placeholder:text-gray-300"
    placeholder="0.00"
    value={roundOff}
    onChange={e => setRoundOff(e.target.value)}
  />
</div>
          <div className="flex justify-between px-2 py-1.5 border-b-2 border-black font-black bg-[#1a1a2e] text-white">
            <span>GRAND TOTAL</span>
            <span>{grandTotalWithRoundOff > 0 ? `Rs. ${grandTotalWithRoundOff.toLocaleString("en-IN")} /-` : "—"}</span>
          </div>
          <div className="flex justify-between items-center px-2 py-1 border-b border-gray-100">
            <span className="text-gray-500 whitespace-nowrap text-[8px]">PARTY INVOICE NO.:</span>
            <InlineEdit value={partyInvoiceNo} onChange={setPartyInvoiceNo} placeholder="invoice no." small className="text-right" />
          </div>
          <div className="flex justify-between items-center px-2 py-1 border-b border-gray-100">
            <span className="text-gray-500 whitespace-nowrap text-[8px]">INVOICE VALUE:</span>
            <InlineEdit value={invoiceValue} onChange={setInvoiceValue} placeholder="enter value" small className="text-right" />
          </div>
          <div className="flex justify-between items-center px-2 py-1 border-b border-gray-100">
            <span className="text-gray-500 whitespace-nowrap text-[8px]">DELIVERY AT:</span>
            <InlineEdit value={deliveryAt} onChange={setDeliveryAt} placeholder={destination || "city"} small className="text-right" />
          </div>
          <div className="flex justify-between items-center px-2 py-1 border-b border-gray-100">
            <span className="text-gray-500 whitespace-nowrap text-[8px]">EWAY BILL NO.:</span>
            <InlineEdit value={ewayBillNo} onChange={setEwayBillNo} placeholder="enter e-way bill" small className="text-right" />
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex flex-col md:flex-row border-t border-black">
        <div className="flex-1 px-2 py-2 border-b md:border-b-0 md:border-r border-black text-[8px]">
          <p className="font-bold mb-0.5 text-[9px]">TERMS & CONDITIONS:</p>
          <p className="text-gray-500 leading-relaxed">1. Carrier not responsible for leakage, breakage, or water damage.</p>
          <p className="text-gray-500">2. Goods booked at owner's risk unless otherwise stated.</p>
          <p className="text-gray-500">3. Claims must be notified within 24 hours of delivery.</p>
          <p className="font-bold mt-1 text-[8px]">E. & O.E.</p>
        </div>
        <div className="w-full md:w-52 px-3 py-2 text-center text-[8px]">
          <div className="h-10 flex items-center justify-center mb-1">
            {company?.signature && <img src={`${process.env.REACT_APP_API_URL}${company.signature}`} alt="sig" className="max-h-10 object-contain" />}
          </div>
          <p className="font-bold">For {company?.company_name || "—"}</p>
          <p className="text-gray-500 text-[7px]">Authorised Signatory</p>
        </div>
      </div>

      {/* Driver Details */}
      <div className="flex flex-col md:flex-row border-t border-black">
        <div className="flex-1 px-2 py-2 border-b md:border-b-0 md:border-r border-black text-[8px]">
          <p className="font-bold text-[9px] mb-1.5 pb-1 border-b border-gray-200">DRIVER DETAILS <span className="font-normal text-gray-400 text-[7px]">(all optional)</span></p>
          <div className="space-y-1.5">
            <div className="flex items-center gap-1">
              <span className="text-gray-500 w-14 shrink-0 text-[8px]">Name:</span>
              <input className="flex-1 bg-transparent outline-none border-b border-dashed border-blue-200 focus:border-blue-500 text-[9px] font-semibold placeholder:text-gray-300"
                placeholder="Driver name" value={driverInput.name}
                onChange={e => setDriverInput((p: DriverInput) => ({ ...p, name: e.target.value }))} />
            </div>
            <div className="flex items-center gap-1">
              <span className="text-gray-500 w-14 shrink-0 text-[8px]">Phone:</span>
              <input type="tel" className="flex-1 bg-transparent outline-none border-b border-dashed border-blue-200 focus:border-blue-500 text-[9px] font-semibold placeholder:text-gray-300"
                placeholder="Phone number" value={driverInput.phone}
                onChange={e => setDriverInput((p: DriverInput) => ({ ...p, phone: e.target.value }))} />
            </div>
            <div className="flex items-center gap-1">
              <span className="text-gray-500 w-14 shrink-0 text-[8px]">License:</span>
              <input className="flex-1 bg-transparent outline-none border-b border-dashed border-blue-200 focus:border-blue-500 text-[9px] font-semibold placeholder:text-gray-300"
                placeholder="License no. (optional)" value={driverInput.license_number}
                onChange={e => setDriverInput((p: DriverInput) => ({ ...p, license_number: e.target.value }))} />
            </div>
          </div>
          <div className="mt-3 pt-1">
            <p className="text-[7px] text-gray-400">Driver Signature:</p>
            <div className="border-b border-dashed border-gray-400 mt-5 mx-1" />
          </div>
        </div>
        <div className="flex-1">
          <NotesPanel notes={notes} setNotes={setNotes} />
        </div>
      </div>

      {/* Payment Mode */}
      <div className="flex flex-col sm:flex-row border-t border-black items-center px-2 py-1.5 gap-2 md:gap-4 bg-gray-50">
        <span className="font-bold text-[9px]">PAYMENT:</span>
        <div className="flex gap-2">
          {["paid", "to_pay", "TO BE BILLED"].map(pt => (
            <button key={pt} type="button" onClick={() => setPaymentType(pt)}
              className={`px-2 py-0.5 text-[8px] font-bold border rounded transition-all ${paymentType === pt ? "bg-[#1a1a2e] text-white border-[#1a1a2e]" : "border-gray-400 text-gray-600 hover:border-gray-800"}`}>
              {pt === "paid" ? "PAID" : pt === "to_pay" ? "TO PAY" : "TO BE BILLED"}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 text-[8px] mt-1 sm:mt-0 ml-0 sm:ml-2 flex-wrap">
          <span className="font-bold text-gray-600">Payable by:</span>
          <label className="flex items-center gap-1 cursor-pointer">
            <input type="radio" checked={paymentPaidBy === "sender"} onChange={() => setPaymentPaidBy("sender")} className="w-3 h-3 accent-blue-700" />
            Sender {sendersList.length > 1 ? `(${sendersList.map((s: any) => s.name).join(", ")})` : (sender?.name ? `(${sender.name})` : "")}
          </label>
          <label className="flex items-center gap-1 cursor-pointer">
            <input type="radio" checked={paymentPaidBy === "receiver"} onChange={() => setPaymentPaidBy("receiver")} className="w-3 h-3 accent-blue-700" />
            Receiver {receiver?.name && `(${receiver.name})`}
          </label>
        </div>
      </div>

      {/* Amount in Words */}
      <div className="px-2 py-1 border-t border-black text-[8px]">
        <span className="font-bold">AMOUNT IN WORDS: </span>
        <span className="italic text-gray-500">{grandTotal > 0 ? numberToWords(grandTotalWithRoundOff) : ""}</span>
      </div>

      <div className="text-center text-[7px] text-gray-400 py-1 border-t border-black bg-gray-50">
        THIS IS A COMPUTER GENERATED LR AND DOES NOT REQUIRE A SIGNATURE.
      </div>
    </div>
  );
}

interface BankDetail {
  id: number;
  company_id: number;
  bank_name: string;
  branch: string | null;
  account_number: string;
  ifsc_code: string | null;
  micr_code: string | null;
  swift_code: string | null;
  account_type: string | null;
  account_holder_name: string | null;
}

// ─── NEW: Bank Selector Dropdown ─────────────────────────────────────────────
// Lets user pick which bank account to print on the LR
// ─── NEW: Bank Selector Dropdown ─────────────────────────────────────────────
// Lets user pick which bank account to print on the LR
function BankSelector({ banks, selectedBank, onSelect }: {
  banks: BankDetail[];
  selectedBank: BankDetail | null;
  onSelect: (bank: BankDetail | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h);
  }, []);

  if (banks.length === 0) return null;

  return (
    <div ref={ref} className="relative inline-block">
      <button type="button" onClick={() => setOpen(v => !v)}
        className="flex items-center gap-1 text-[8px] text-blue-600 hover:text-blue-800 border-b border-dashed border-blue-300 hover:border-blue-600 transition-colors font-bold">
        {selectedBank ? `${selectedBank.bank_name} ···${selectedBank.account_number.slice(-4)}` : "Select bank"}
        <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><polyline points="6 9 12 15 18 9" /></svg>
      </button>
      {open && (
        <div className="absolute z-50 bottom-full left-0 mb-1 w-72 bg-white border-2 border-blue-400 rounded-lg shadow-xl overflow-hidden">
          <div className="px-3 py-2 border-b border-blue-100 bg-blue-50">
            <span className="text-[9px] font-black text-blue-800 uppercase tracking-wide">Select Bank Account</span>
          </div>
          <div className="max-h-48 overflow-y-auto">
            {banks.map(b => (
              <button key={b.id} type="button"
                onClick={() => { onSelect(b); setOpen(false); }}
                className={`w-full text-left px-3 py-2.5 hover:bg-blue-50 transition-colors border-b border-gray-50 ${selectedBank?.id === b.id ? "bg-blue-50" : ""}`}>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-black text-gray-900">{b.bank_name}</span>
                  {b.account_type && <span className="text-[8px] text-blue-600 bg-blue-50 border border-blue-200 rounded px-1">{b.account_type}</span>}
                </div>
                {b.account_holder_name && <div className="text-[8px] text-gray-500 mt-0.5">{b.account_holder_name}</div>}
                <div className="text-[8px] text-gray-500 font-mono mt-0.5">
                  A/C: {b.account_number}
                  {b.ifsc_code && ` · IFSC: ${b.ifsc_code}`}
                </div>
                {b.branch && <div className="text-[8px] text-gray-400">{b.branch}</div>}
              </button>
            ))}
          </div>
          <div className="border-t border-blue-100 px-3 py-1.5">
            <button type="button" onClick={() => { onSelect(null); setOpen(false); }}
              className="text-[8px] text-gray-400 hover:text-red-500 transition-colors">
              Clear selection
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
function LrNumberInput({ invoiceNumber, setInvoiceNumber, displayLrNo, companyId, excludeInvoiceDbId }: {
  invoiceNumber: string;
  setInvoiceNumber: (v: string) => void;
  displayLrNo: string;
  companyId: number | null;
  excludeInvoiceDbId?: number | null;
}) {
  const [editing, setEditing] = useState(false);
  const [checking, setChecking] = useState(false);
  const [lrError, setLrError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);
  useEffect(() => { setLrError(null); }, [invoiceNumber]);

  async function checkLrExists(lrNo: string) {
    const trimmed = lrNo.trim();
    if (!trimmed || !companyId) return;

    setChecking(true); setLrError(null);
    try {
      const params: Record<string, any> = { company: companyId };
      if (excludeInvoiceDbId) params.excludeId = excludeInvoiceDbId;

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/invoiceapi/invoice/number/${encodeURIComponent(trimmed)}`,
        { params }
      );
      if (res.status === 200 && res.data) {
        setLrError(`LR No. "${trimmed}" already exists for this company.`);
      }
    } catch (err: any) {
      if (err?.response?.status !== 404) console.warn("LR check failed:", err);
    } finally {
      setChecking(false);
    }
  }

  return (
    <div>
      <span className="text-[7px] text-gray-500 uppercase block">
        LR / GR No. <span className="text-red-500">*required</span>
      </span>
      {editing ? (
        <input ref={inputRef}
          className={`text-sm font-black tracking-widest border-b-2 outline-none bg-transparent ${lrError ? "border-red-500 text-red-700" : "border-blue-500 text-blue-900"}`}
          value={invoiceNumber}
          onChange={e => { setInvoiceNumber(e.target.value); setLrError(null); }}
          onBlur={async () => { setEditing(false); await checkLrExists(invoiceNumber); }}
          onKeyDown={async e => { if (e.key === "Enter") { setEditing(false); await checkLrExists(invoiceNumber); } }}
          placeholder={displayLrNo} />
      ) : (
        <span tabIndex={0}
        role="button"
        onClick={() => setEditing(true)}
        onFocus={() => setEditing(true)}
        onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setEditing(true); } }}
          className={`cursor-text border-b border-dashed text-sm font-black tracking-widest block ${lrError ? "border-red-400 text-red-700" : "border-blue-400 text-blue-900 hover:border-blue-700"}`}>
          {invoiceNumber || <span className="text-blue-400 italic font-normal text-[10px]">{displayLrNo || "click to enter LR no."}</span>}
          {checking && <span className="ml-2 text-[8px] text-gray-400 animate-pulse font-normal">checking…</span>}
        </span>
      )}
      {lrError && (
        <div className="mt-1 flex items-center gap-1 bg-red-50 border border-red-300 rounded px-2 py-1">
          <span className="text-[9px] text-red-600 font-semibold">{lrError}</span>
        </div>
      )}
    </div>
  );
}
// Fields that must be present for a bill to count as "complete".
// Adjust this list to match what actually matters for your billing workflow.
function getMissingFields({
  company, sender, receiver, vehicle, driverInput, origin, destination, items, invoiceNumber,
}: {
  company: Company | null; sender: User | null; receiver: User | null; vehicle: Vehicle | null;
  driverInput: DriverInput; origin: string; destination: string; items: Item[]; invoiceNumber: string;
}): string[] {
  const missing: string[] = [];
  if (!company) missing.push("Company");
  if (!sender) missing.push("Consignor (Sender)");
  if (!receiver) missing.push("Consignee (Receiver)");
  if (!vehicle) missing.push("Vehicle");
  if (!origin.trim()) missing.push("Origin");
  // if (!destination.trim()) missing.push("Destination");
  // if (!driverInput.name.trim()) missing.push("Driver Name");
  // if (!driverInput.phone.trim()) missing.push("Driver Phone");
  // Consider items "present" if any item has any field filled in (name, amount, qty, or rate)
  // Check if at least one item has any field filled in (name, qty, ratePerQtl), but specifically require amount to be filled for any item
  const hasAnyItem = items.some(i =>
    String(i.name || "").trim() !== "" ||
    String(i.hsnCode || "").trim() !== "" ||
    String(i.qty || "").trim() !== "" ||
    String(i.ratePerQtl || "").trim() !== "" ||
    (String(i.amount || "").trim() !== "" && Number(i.amount) > 0)
  );
  if (!hasAnyItem) {
    missing.push("Items");
  } 
  // else {
  //   // For each item, determine if it's "completed" according to the new rule:
  //   // - If amount is present (non-empty), it's completed
  //   // - OR if BOTH qty and ratePerQtl are present (non-empty), it's completed
  //   // Only if ALL items are incomplete by this rule, push "Amount" as missing
  //   const allIncomplete = items.every(i => {
  //     const amountPresent = String(i.amount || "").trim() !== "";
  //     const qtyPresent = String(i.qty || "").trim() !== "";
  //     const ratePresent = String(i.ratePerQtl || "").trim() !== "";
  //     return !(amountPresent || (qtyPresent && ratePresent));
  //   });

  //   if (allIncomplete) {
  //     missing.push("Amount");
  //   }
  // }
  else {
    // Check if at least one item is "complete": (qty && ratePerQtl present) OR (amount present)
    const hasCompletedItem = items.some(i => {
      const qtyFilled = String(i.qty || "").trim() !== "";
      const rateFilled = String(i.ratePerQtl || "").trim() !== "";
      const amountFilled = String(i.amount || "").trim() !== "" && Number(i.amount) > 0;
 
      return (qtyFilled && rateFilled) || amountFilled;
    });
    if (!hasCompletedItem) missing.push("Amount");
  }
  if (!invoiceNumber.trim()) missing.push("LR Number");
  return missing;
}
// ─── Main: EditConsignment ────────────────────────────────────────────────────
export const EditConsignment: React.FC = () => {
  const { id: invoiceDbId } = useParams<{ id: string }>();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [companies, setCompanies] = useState<Company[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [transporters, setTransporters] = useState<Transporter[]>([]);

  const [company, setCompany] = useState<Company | null>(null);
  const [senders, setSenders] = useState<User[]>([]);
  const sender = senders[0] || null;
  const setSender = (val: User | null | ((prev: User | null) => User | null)) => {
    if (typeof val === 'function') {
      setSenders(prev => {
        const cur = prev[0] || null;
        const next = val(cur);
        if (!next) return prev.slice(1);
        return [next, ...prev.slice(1)];
      });
    } else if (!val) {
      setSenders([]);
    } else {
      setSenders(prev => prev.some(s => s.id === val.id) ? prev : [val, ...prev.slice(1)]);
    }
  };
  const [receiver, setReceiver] = useState<User | null>(null);
  const [vehicle, setVehicle] = useState<Vehicle | null>(null);
  const [driverInput, setDriverInput] = useState<DriverInput>({ name: "", phone: "", license_number: "" });
  const [invoiceNumber, setInvoiceNumber] = useState("");
  
  const [selectedDate, setSelectedDate] = useState(() => new Date().toISOString().slice(0, 10));

  const [origin, setOrigin] = useState("");
  const [destination, setDestination] = useState("");

  // ── CHANGED: load internalDescription from inv.internalDescription
  const [description, setDescription] = useState("");

  const [subject, setSubject] = useState("Kishangarh");
  const [items, setItems] = useState<Item[]>([{ name: "", hsnCode: "", qty: "", weight: "", ratePerQtl: "", amount: "" }]);
  const [paymentType, setPaymentType] = useState("to_pay");
  const [paymentPaidBy, setPaymentPaidBy] = useState<"sender" | "receiver">("receiver");
  const [hasBroker, setHasBroker] = useState(false);
  const [brokerPaidBy, setBrokerPaidBy] = useState<"sender" | "receiver" | null>(null);
  const [brokerId, setBrokerId] = useState<number | null>(null);

  // ── CHANGED: load notes from inv.notesitems (JSON string → Note[])
  const [notes, setNotes] = useState<Note[]>([]);

  const [modal, setModal] = useState<null | "company" | "sender" | "receiver" | "vehicle">(null);
  const [submitting, setSubmitting] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showShareOptions, setShowShareOptions] = useState(false);
  const [partyInvoiceNo, setPartyInvoiceNo] = useState("");
  const [invoiceValue, setInvoiceValue] = useState("");
  const [deliveryAt, setDeliveryAt] = useState("");
  const [ewayBillNo, setEwayBillNo] = useState("");
  const [roundOff, setRoundOff] = useState("");
  const [forceCompleted, setForceCompleted] = useState(false);

const missingFields = getMissingFields({ company, sender, receiver, vehicle, driverInput, origin, destination, items, invoiceNumber });
const billingStatus: "pending" | "completed" = forceCompleted || missingFields.length === 0 ? "completed" : "pending";

    // NEW: company bank details state
    const [companyBanks, setCompanyBanks] = useState<BankDetail[]>([]);
    const [selectedBank, setSelectedBank] = useState<BankDetail | null>(null);
    const [originalSnapshot, setOriginalSnapshot] = useState<any>(null);
    const [originalInvoice, setOriginalInvoice] = useState<any>(null);

    // NEW: Fetch company bank details whenever the selected company changes
    useEffect(() => {
      if (!company) { setCompanyBanks([]); setSelectedBank(null); return; }
      axios.get(`${process.env.REACT_APP_API_URL}/companyAuth/company/${company.id}/bank`)
        .then(res => {
          const banks: BankDetail[] = res.data || [];
          setCompanyBanks(banks);
          // Auto-select first bank if only one exists, else clear selection
          setSelectedBank(banks.length === 1 ? banks[0] : null);
        })
        .catch(() => { setCompanyBanks([]); setSelectedBank(null); });
    }, [company]);

  const loadData = useCallback(async () => {
    try {
      const [compRes, userRes, vehRes, trRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/companyAuth/companies`),
        axios.get(`${process.env.REACT_APP_API_URL}/auth/alluser`),
        axios.get(`${process.env.REACT_APP_API_URL}/auth/vehicles`),
        axios.get(`${process.env.REACT_APP_API_URL}/auth/transporters`),
      ]);
      return {
        companies: compRes.data || [],
        users: userRes.data || [],
        vehicles: vehRes.data || [],
        transporters: trRes.data || [],
      };
    } catch (e) {
      throw new Error("Failed to load reference data");
    }
  }, []);

  useEffect(() => {
    if (!invoiceDbId) { setLoadError("No invoice ID in URL."); setLoading(false); return; }

    (async () => {
      try {
        const [refs, invoiceRes] = await Promise.all([
          loadData(),
          axios.get(`${process.env.REACT_APP_API_URL}/invoiceapi/invoice/${invoiceDbId}`),
        ]);

        const { companies: comps, users: usrs, vehicles: vehs, transporters: trans } = refs;
        setCompanies(comps);
        setUsers(usrs);
        setVehicles(vehs);
        setTransporters(trans);

        const inv = invoiceRes.data;
        setOriginalInvoice(inv);
        setInvoiceNumber(inv["InvoiceNo."] || inv.InvoiceNo || "");
  // console.log("inv ", inv)
     
        const itemsRes = await axios.get(
          // `${process.env.REACT_APP_API_URL}/invoiceapi/invoice_items/invoice/${invoiceNo}`
          `${process.env.REACT_APP_API_URL}/invoiceapi/invoice_items/invoice-db/${invoiceDbId}`
        );

        // Company / sender / receiver / vehicle
        setForceCompleted(!!inv.billing_status_forced);
        setCompany(comps.find((c: Company) => c.id === inv.company) || null);

        // Restore multiple senders or single sender
        let loadedSenders: User[] = [];
        if (inv.multiple_senders) {
          try {
            const parsed = typeof inv.multiple_senders === "string" ? JSON.parse(inv.multiple_senders) : inv.multiple_senders;
            if (Array.isArray(parsed)) {
              loadedSenders = parsed.map((sid: any) => usrs.find((u: User) => u.id === (typeof sid === "object" ? sid.id : sid))).filter(Boolean);
            }
          } catch (e) {
            console.error("Error parsing multiple_senders", e);
          }
        }

        // Fallback to notesitems if multiple_senders wasn't populated or returned empty
        if (loadedSenders.length === 0 && inv.notesitems) {
          try {
            const parsedNotes = JSON.parse(inv.notesitems);
            if (parsedNotes && !Array.isArray(parsedNotes) && Array.isArray(parsedNotes.multiple_senders)) {
              loadedSenders = parsedNotes.multiple_senders.map((sid: any) => usrs.find((u: User) => u.id === (typeof sid === "object" ? sid.id : sid))).filter(Boolean);
            }
          } catch (e) {
            // ignore
          }
        }

        // Fallback to primary sender
        if (loadedSenders.length === 0 && inv.sender) {
          const s = usrs.find((u: User) => u.id === inv.sender);
          if (s) loadedSenders = [s];
        }
        setSenders(loadedSenders);

        setReceiver(usrs.find((u: User) => u.id === inv.receiver) || null);
        setVehicle(vehs.find((v: Vehicle) => v.id === inv.vehicles) || null);
        setOriginalSnapshot({
          receiver: usrs.find((u: User) => u.id === inv.receiver) || null,
          sender: loadedSenders[0] || null,
          // add any other fields you want tracked
        });
        // Scalar fields
        setOrigin(inv.source || "");
        setDestination(inv.destination || "");
        setSubject(inv.subjects || inv.subject || "Kishangarh");
        setPaymentType(inv.paymentStatus || inv.paymentType || "to_pay");
        setEwayBillNo(inv.EWAYBILLNO || "");
        setPdfUrl(inv.link ? `${process.env.REACT_APP_API_URL}${inv.link}` : null);
        setPartyInvoiceNo(inv.party_invoice_no || "");
        setInvoiceValue(inv.invoice_value || "");
        setRoundOff(inv.round_off != null ? String(inv.round_off) : "");
   

        if (inv.created_at) setSelectedDate(inv.created_at.slice(0, 10));
   

        if (inv.paymentPayableConsignor) {
          if (inv.paymentPayableConsignor === inv.sender) setPaymentPaidBy("sender");
          else if (inv.paymentPayableConsignor === inv.receiver) setPaymentPaidBy("receiver");
        }

        // Broker
        if (inv.broker) {
          setHasBroker(true);
          setBrokerId(inv.broker);
          if (inv.brokerPaidBy === "sender" || inv.paymentPayableConsignor === inv.sender) {
            setBrokerPaidBy("sender");
          } else if (inv.brokerPaidBy === "receiver" || inv.paymentPayableConsignor === inv.receiver) {
            setBrokerPaidBy("receiver");
          }
        }

        // Driver
        if (inv.driver) {
          try {
            const driverRes = await axios.get(`${process.env.REACT_APP_API_URL}/auth/drivers/${inv.driver}`);
            const d = driverRes.data;
            setDriverInput({ name: d.name || "", phone: d.phone || "", license_number: d.license_number || "" });
          } catch { /* leave blank */ }
        }

        // ── CHANGED: restore internalDescription from saved field
        if (inv.internalDescription) {
          setDescription(inv.internalDescription);
        }

        // ── CHANGED: restore notes from saved notesitems JSON string
        if (inv.notesitems) {
          try {
            const parsed = JSON.parse(inv.notesitems);
            const notesArray = Array.isArray(parsed) ? parsed : (Array.isArray(parsed?.notes) ? parsed.notes : []);
            if (Array.isArray(notesArray)) {
              // Each item must have id, text, color — guard against malformed data
              const validNotes: Note[] = notesArray
                .filter((n: any) => n && typeof n.text === "string")
                .map((n: any) => ({
                  id: n.id ? String(n.id) : Date.now().toString() + Math.random(),
                  text: n.text,
                  color: n.color || NOTE_COLORS[0].bg,
                }));
              setNotes(validNotes);
            }
          } catch {
            // Malformed JSON — start with empty notes
            setNotes([]);
          }
        }

        // Items
        const rawItems: any[] = itemsRes.data || [];
        if (rawItems.length > 0) {
          // setItems(rawItems.map((row: any) => ({
          //   name: row.description || "",
          //   qty: row.qty ? String(row.qty) : "",
          //   weight: "",
          //   ratePerQtl: "",
          //   amount: row.amount ? String(row.amount) : "",
          // })));

          setItems(rawItems.map((row: any) => ({
            name: row.description || "",
            hsnCode: (row.hsm_code && row.hsm_code !== "-") ? row.hsm_code : ((row.hsn_code && row.hsn_code !== "-") ? row.hsn_code : (row.hsn || "")),
            qty: row.qty ? String(row.qty) : "",
            weight: "",
            ratePerQtl: row.rate ? String(row.rate) : "",   // keep rate if stored
            amount: row.amount ? String(row.amount) : "",    // amount takes priority in resolveItemAmount
            uom: row.uom || "Ton",                           // restore per-item UOM
          })));
        }

        setLoading(false);
      } catch (e: any) {
        console.error(e);
        setLoadError(e?.message || "Failed to load invoice.");
        setLoading(false);
      }
    })();
  }, [invoiceDbId, loadData]);

  const updateItem = (idx: number, key: keyof Item, value: string) =>
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, [key]: value } : it));

  const grandTotal = items.reduce((acc, it) => acc + resolveItemAmount(it), 0);
  function buildChangeLog() {
    const changes: any[] = [];
    const ts = new Date().toISOString();
  
    if (originalSnapshot?.receiver?.id !== receiver?.id) {
      changes.push({
        field: "consignee",
        from: originalSnapshot?.receiver
          ? { id: originalSnapshot.receiver.id, name: originalSnapshot.receiver.name, address: originalSnapshot.receiver.address }
          : null,
        to: receiver ? { id: receiver.id, name: receiver.name, address: receiver.address } : null,
        changed_at: ts,
      });
    }
    // repeat for sender, vehicle, origin/destination, etc. as needed
  
    return changes;
  }
  async function handleSubmit() {
    if (!company) { alert("Please select a company."); return; }

    const invoiceId = invoiceNumber.trim() ||
      (origin && destination && vehicle
        ? generateInvoiceId(origin, destination, vehicle.vehicle_number, selectedDate)
        : `LR${Date.now()}`);

    setSubmitting(true);
    try {
      const transporter = vehicle
        ? transporters.find(t => t.id === (vehicle as any).transporter_id) || null
        : null;

      const blob = await pdf(
        <InvoicePDF
          company={company} sender={sender} senders={senders} receiver={receiver}
          selectedVehicle={vehicle} origin={origin} destination={destination}
          items={items} paymentType={paymentType}
          invoiceId={invoiceId} selectedDate={selectedDate} subject={subject}
          notes={notes} driverInput={driverInput}
          partyInvoiceNo={partyInvoiceNo} invoiceValue={invoiceValue}
          deliveryAt={deliveryAt} ewayBillNo={ewayBillNo}
          selectedBank={selectedBank}
          Round_off = {roundOff}
        />
      ).toBlob();

      const formData = new FormData();
      // formData.append("invoicePdf", blob, `invoice_${invoiceId}.pdf`);
      formData.append("invoicePdf", blob, `invoice_${company.company_name}_${invoiceId}_${Date.now()}.pdf`);
      formData.append("invoiceId", invoiceId);
      const uploadResp = await axios.post(
        `${process.env.REACT_APP_API_URL}/invoiceapi/uploadpdf`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      const link = uploadResp.data?.link
        ? `${process.env.REACT_APP_API_URL}${uploadResp.data.link}`
        : (pdfUrl || "");
      if (link) setPdfUrl(link);

      // ── CHANGED: serialize notes and multiple senders for PUT, include internalDescription
      const primarySenderId = senders[0]?.id ?? null;
      const allSenderIds = senders.map(s => s.id);
      const notesData = notes.map(n => ({ id: n.id, text: n.text, color: n.color }));
      const notesForApi = JSON.stringify({
        notes: notesData,
        multiple_senders: allSenderIds,
      });
      const newChanges = buildChangeLog();
      const existingLog: any[] = originalInvoice?.change_log ? JSON.parse(originalInvoice.change_log) : [];
      const updatedLog = [...existingLog, ...newChanges];

      await axios.put(`${process.env.REACT_APP_API_URL}/invoiceapi/invoice/${invoiceDbId}`, {
        company: company.id,
        sender: primarySenderId,
        multiple_senders: JSON.stringify(allSenderIds),
        receiver: receiver?.id ?? null,
        broker: hasBroker ? (brokerId ?? null) : null,
        transporter: transporter?.id ?? null,
        driver: vehicle?.driver_id ?? null,
        vehicles: vehicle?.id ?? null,
        payment: grandTotal > 0 ? `Rs. ${grandTotal.toLocaleString("en-IN")} /-` : "0",
        source: origin || null,
        destination: destination || null,
        receiverAmountStatatus: "Pending",
        senderAmountStatatus: "Pending",
        transporterAmountStatatus: "Pending",
        paymentStatus: paymentType,
        paymentPayableConsignor: paymentPaidBy === "sender"
          ? primarySenderId
          : (receiver?.id ?? null),
        created_at: selectedDate,
        ItemsDescription: JSON.stringify(items.filter(i => i.name).map(i => i.name)),
        InvoiceNo: invoiceId,
        EWAYBILLNO: ewayBillNo,
        subjects: subject || null,
        link: uploadResp.data?.link || "",
        party_invoice_no: partyInvoiceNo || null,
        invoice_value: invoiceValue || null,
        // ── CHANGED: send internalDescription and notesitems
        internalDescription: description || null,
        notesitems: notesForApi,
        round_off: roundOff || null,
        change_log: newChanges.length > 0 ? JSON.stringify(updatedLog) : (originalInvoice?.change_log ?? null),
        billing_status: billingStatus,
        billing_status_forced: forceCompleted,
      });

      // Update items
    // Update ALL items using the original invoice number
// const validItems = items.filter(i => i.name.trim());
// Do not filter items by `name`. Allow empty names.
if (items.length > 0) {
  await axios.put(
    `${process.env.REACT_APP_API_URL}/invoiceapi/invoice_items/invoice-db/${invoiceDbId}`,
    items.map(item => ({
      description: item.name,
      hsm_code: item.hsnCode ? item.hsnCode.trim() : "-",
      uom: item.uom || "Ton",
      qty: item.qty,
      rate: item.ratePerQtl,
      amount: String(resolveItemAmount(item)),
    }))
  );
}

      setShowSuccess(true);
    } catch (e) {
      alert("Failed to update: " + (e as any)?.message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleAddCompany(data: Record<string, string>) {
    await axios.post(`${process.env.REACT_APP_API_URL}/companyAuth/company`, {
      company_name: data.company_name, address: data.address, pancard: data.pancard,
      gst_number: data.gst_number, email: data.email, password: data.password || "password123",
      HSN_SAC: "", prefix: "", postfix: "", separatorsign: ""
    });
    const res = await axios.get(`${process.env.REACT_APP_API_URL}/companyAuth/companies`);
    setCompanies(res.data || []);
  }

  async function handleAddUser(data: Record<string, string>, role: string) {
    const res = await axios.post(`${process.env.REACT_APP_API_URL}/auth/admin/users`, {
      name: data.name, email: data.email, number: data.number,
      password: data.password || "password123", role, address: data.address,
      gst_number: data.gst_number || null,
    });
    const userRes = await axios.get(`${process.env.REACT_APP_API_URL}/auth/alluser`);
    setUsers(userRes.data || []);
    if (role === "consignor" && res.data?.id) {
      setSenders(prev => [...prev, {
        id: res.data.id,
        name: data.name,
        email: data.email,
        number: data.number,
        password: "",
        role: "consignor",
        deletedUser: 0,
        createdAt: "",
        updatedAt: "",
        address: data.address || null,
        city: null, state: null, pincode: null,
        gst_number: data.gst_number || null,
        pan_number: null, aadhaar_number: null, license_number: null,
        bank_name: null, bank_account_number: null, bank_ifsc: null, account_holder_name: null
      } as User]);
    }
  }

  async function handleAddVehicle(data: Record<string, string>) {
    await axios.post(`${process.env.REACT_APP_API_URL}/auth/vehicles`, {
      vehicle_number: data.vehicle_number, registration_number: data.vehicle_number,
      vehicle_type: data.vehicle_type || "Truck", capacity_kg: 1000,
      transporter_id: null, driver_id: null
    });
    const res = await axios.get(`${process.env.REACT_APP_API_URL}/auth/vehicles`);
    setVehicles(res.data || []);
  }

  const transporter = vehicle
    ? transporters.find(t => t.id === (vehicle as any).transporter_id) || null
    : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <svg className="w-10 h-10 animate-spin text-blue-700 mx-auto mb-3" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" strokeOpacity={0.2} />
            <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round" />
          </svg>
          <p className="text-slate-600 font-semibold text-sm">Loading invoice #{invoiceDbId}…</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="bg-white rounded-2xl border-2 border-red-300 p-8 text-center max-w-sm mx-4">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </div>
          <h2 className="font-black text-red-800 mb-1">Failed to Load</h2>
          <p className="text-sm text-gray-500">{loadError}</p>
          <button onClick={() => window.history.back()} className="mt-4 px-4 py-2 bg-slate-800 text-white rounded-lg text-sm font-bold">← Go Back</button>
        </div>
      </div>
    );
  }

 
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100 p-2 md:p-6 lg:p-8">
      <div className="max-w-full md:max-w-3xl lg:max-w-5xl mx-auto mb-[80px] md:mb-[60px]">

        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-2">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-100 border border-amber-300 rounded-lg text-amber-800 text-xs font-bold">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
                EDIT MODE
              </span>
              <span className="text-xs text-slate-400 font-mono">ID #{invoiceDbId}</span>
            </div>
            <h1 className="text-2xl font-black text-slate-900 tracking-tight">Edit Consignment</h1>
            <p className="text-[13px] sm:text-sm text-slate-500 mt-0.5">
              Editing LR&nbsp;
              <span className="font-bold text-blue-700">{invoiceNumber || `#${invoiceDbId}`}</span>
              &nbsp;·&nbsp;
              <span className="text-red-500 font-semibold">Company</span> required
            </p>
          </div>
          <div className="flex items-center gap-2 bg-white border-2 border-blue-200 rounded-xl px-2 sm:px-3 py-2 w-full sm:w-auto">
            <label className="text-xs font-bold text-slate-600">Date:</label>
            <input
              type="date"
              className="text-xs font-bold text-blue-900 bg-transparent focus:outline-none w-full sm:w-auto"
              value={selectedDate}
              onChange={e => setSelectedDate(e.target.value)}
            />
          </div>
        </div>

        {/* Broker Toggle */}
        <div className="bg-white rounded-2xl border-2 border-amber-200 px-3 sm:px-5 py-2 sm:py-3 mb-4 flex flex-col md:flex-row flex-wrap items-start md:items-center gap-2 md:gap-4">
          <span className="text-sm font-black text-amber-800">🤝 Broker Involved?</span>
          <label className="flex items-center gap-2 cursor-pointer">
            <div
              onClick={() => { setHasBroker(v => !v); if (hasBroker) setBrokerPaidBy(null); }}
              className={`w-11 h-6 rounded-full relative transition-all ${hasBroker ? "bg-amber-500" : "bg-gray-200"}`}>
              <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${hasBroker ? "left-5" : "left-0.5"}`} />
            </div>
            <span className="text-sm font-bold text-gray-700">{hasBroker ? "Yes" : "No"}</span>
          </label>
          {hasBroker && (
            <div className="flex flex-wrap items-center gap-2 sm:gap-3 ml-0 md:ml-2">
              <span className="text-sm font-bold text-gray-600">Commission paid by:</span>
              {(["sender", "receiver"] as const).map(party => (
                <label key={party}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border-2 text-sm font-bold cursor-pointer transition-all ${brokerPaidBy === party ? "bg-amber-500 border-amber-500 text-white" : "bg-white border-amber-200 text-gray-700 hover:border-amber-400"}`}>
                  <input type="radio" name="brokerPaidBy" value={party} checked={brokerPaidBy === party} onChange={() => setBrokerPaidBy(party)} className="hidden" />
                  {party.charAt(0).toUpperCase() + party.slice(1)}
                  {party === "sender" && (senders.length > 0) && (
                    <span className="opacity-70 text-xs font-normal">
                      ({senders.map(s => s.name).join(", ")})
                    </span>
                  )}
                  {party === "receiver" && receiver?.name && <span className="opacity-70 text-xs font-normal">({receiver.name})</span>}
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Invoice Preview */}
        <div className="w-full sm:max-w-[500px] md:max-w-[650px] lg:max-w-[75vw] mx-auto overflow-x-auto" style={{ zoom: 1.45, minWidth: 0 }}>
          <InvoicePreview
            company={company} setCompany={setCompany} companies={companies}
            sender={sender} setSender={setSender}
            senders={senders} setSenders={setSenders}
            receiver={receiver} setReceiver={setReceiver}
            vehicle={vehicle} setVehicle={setVehicle}
            driverInput={driverInput} setDriverInput={setDriverInput}
            users={users} vehicles={vehicles}
            origin={origin} setOrigin={setOrigin}
            destination={destination} setDestination={setDestination}
            subject={subject} setSubject={setSubject}
            invoiceNumber={invoiceNumber} setInvoiceNumber={setInvoiceNumber}
            selectedDate={selectedDate}
            items={items} setItems={setItems} updateItem={updateItem}
            paymentType={paymentType} setPaymentType={setPaymentType}
            paymentPaidBy={paymentPaidBy} setPaymentPaidBy={setPaymentPaidBy}
            transporter={transporter}
            hasBroker={hasBroker} brokerPaidBy={brokerPaidBy} description={description}
            notes={notes} setNotes={setNotes}
            setModal={setModal}
            partyInvoiceNo={partyInvoiceNo} setPartyInvoiceNo={setPartyInvoiceNo}
            invoiceValue={invoiceValue} setInvoiceValue={setInvoiceValue}
            deliveryAt={deliveryAt} setDeliveryAt={setDeliveryAt}
            ewayBillNo={ewayBillNo} setEwayBillNo={setEwayBillNo}
            companyBanks={companyBanks}
            selectedBank={selectedBank}
            setSelectedBank={setSelectedBank}
            excludeInvoiceDbId={invoiceDbId ? Number(invoiceDbId) : null}
            roundOff={roundOff}
            setRoundOff = {setRoundOff}
          />
        </div>

        {/* ── CHANGED: Internal Description — pre-filled from inv.internalDescription */}
        <div className="mt-4 bg-white rounded-2xl border-2 border-blue-200 px-3 sm:px-5 py-2 sm:py-3">
          <label className="text-xs font-bold text-slate-600 block mb-1">
            Internal Description <span className="font-normal text-slate-400">(not printed on LR)</span>
          </label>
          <textarea
            className="w-full border border-blue-100 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:border-blue-400 resize-none bg-blue-50"
            rows={2} placeholder="Optional internal notes about this consignment…"
            value={description} onChange={e => setDescription(e.target.value)}
          />
        </div>

        {/* Submit */}
        <div className={`mt-4 rounded-2xl border-2 px-4 py-3 flex flex-col gap-2 ${billingStatus === "completed" ? "border-green-300 bg-green-50" : "border-amber-300 bg-amber-50"}`}>
  <div className="flex items-center justify-between">
    <span className={`text-sm font-black ${billingStatus === "completed" ? "text-green-700" : "text-amber-700"}`}>
      Billing Status: {billingStatus === "completed" ? "✓ Completed" : "⏳ Pending"}
    </span>
    <label className="flex items-center gap-2 cursor-pointer text-xs font-bold text-gray-600">
      <input type="checkbox" checked={forceCompleted} onChange={e => setForceCompleted(e.target.checked)} className="w-4 h-4 accent-green-600" />
      Mark as completed anyway
    </label>
  </div>
  {missingFields.length > 0 && !forceCompleted && (
    <p className="text-xs text-amber-700">
      Missing: <span className="font-semibold">{missingFields.join(", ")}</span>
    </p>
  )}
  {missingFields.length > 0 && forceCompleted && (
    <p className="text-xs text-gray-500 italic">Overriding — this bill will be marked complete despite missing: {missingFields.join(", ")}</p>
  )}
</div>
        <div className="mt-6 flex flex-col sm:flex-row justify-end gap-3">
          <button type="button" onClick={() => window.history.back()}
            className="px-6 py-3 border-2 border-slate-300 rounded-xl text-sm font-bold text-slate-600 hover:border-slate-500 transition-colors w-full sm:w-auto">
            ← Cancel
          </button>

          <button type="button" onClick={handleSubmit} disabled={submitting}
            className="px-10 py-3 bg-amber-600 hover:bg-amber-700 text-white rounded-xl text-sm font-extrabold transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 w-full sm:w-auto justify-center">
            {submitting && (
              <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" strokeOpacity={0.25} />
                <path d="M12 2a10 10 0 0 1 10 10" />
              </svg>
            )}
            {submitting ? "Saving…" : "Save Changes →"}
          </button>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccess && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-2">
          <div className="bg-white rounded-2xl shadow-2xl border-2 border-amber-400 w-full max-w-md sm:max-w-lg mx-2 p-6 sm:p-8 text-center">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" /></svg>
            </div>
            <h2 className="text-xl font-black text-gray-900 mb-1">Changes Saved!</h2>
            <p className="text-sm text-gray-500 mb-2">LR Number:</p>
            <p className="font-mono font-black text-blue-700 text-lg mb-6">{invoiceNumber}</p>
            <div className="flex flex-col sm:flex-row gap-3">
              {pdfUrl && (
                <button onClick={() => window.open(pdfUrl, "_blank")}
                  className="flex-1 py-3 bg-[#1a1a2e] hover:bg-[#2d2d4e] text-white rounded-xl font-bold text-sm transition-colors">
                  View PDF
                </button>
              )}
              <button onClick={() => exportConsignmentToExcel({
                company, sender, senders, receiver, vehicle, driverInput, transporter: vehicle ? transporters.find(t => t.id === (vehicle as any).transporter_id) : null,
                invoiceNumber, selectedDate, origin, destination, subject, items,
                paymentType, paymentPaidBy, selectedBank, partyInvoiceNo, invoiceValue,
                deliveryAt, ewayBillNo, roundOff, hasBroker, brokerPaidBy, notes, description
              })} className="flex-1 py-3 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl font-bold text-sm transition-colors">
                Download Excel
              </button>
              <button
                onClick={() => { if (pdfUrl && navigator.share) navigator.share({ title: "LR Invoice", url: pdfUrl! }); else setShowShareOptions(true); }}
                className="flex-1 py-3 border-2 border-blue-300 rounded-xl font-bold text-sm text-blue-700 hover:bg-blue-50 transition-colors">
                Share
              </button>
              <button onClick={() => window.history.back()}
                className="flex-1 py-3 border-2 border-gray-200 rounded-xl font-bold text-sm text-gray-600 hover:border-gray-400 transition-colors">
                ← Back
              </button>
            </div>
            {showShareOptions && pdfUrl && (
              <div className="mt-4 border-t pt-4">
                <p className="text-xs font-bold text-blue-700 mb-2">Share link:</p>
                <input className="w-full border px-2 py-1.5 rounded text-xs bg-blue-50" value={pdfUrl} readOnly onFocus={e => e.target.select()} />
                <button className="mt-2 text-xs text-blue-700 font-bold" onClick={() => navigator.clipboard.writeText(pdfUrl!)}>
                  Copy to clipboard
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modals */}
      {modal === "company" && (
        <AddModal title="Company"
          fields={[
            { key: "company_name", label: "Company Name", required: true },
            { key: "address", label: "Address" },
            { key: "email", label: "Email" },
            { key: "pancard", label: "PAN Card" },
            { key: "gst_number", label: "GST Number" },
          ]}
          onClose={() => setModal(null)} onSave={handleAddCompany} />
      )}
      {modal === "sender" && (
        <AddModal title="Sender (Consignor)"
          fields={[
            { key: "name", label: "Full Name", required: true },
            { key: "number", label: "Phone Number", required: true },
            { key: "email", label: "Email", required: true },
            { key: "address", label: "Address" },
            { key: "gst_number", label: "GST Number" },
          ]}
          onClose={() => setModal(null)} onSave={d => handleAddUser(d, "consignor")} />
      )}
      {modal === "receiver" && (
        <AddModal title="Receiver (Consignee)"
          fields={[
            { key: "name", label: "Full Name", required: true },
            { key: "number", label: "Phone Number", required: true },
            { key: "email", label: "Email", required: true },
            { key: "address", label: "Address" },
            { key: "gst_number", label: "GST Number" },
          ]}
          onClose={() => setModal(null)} onSave={d => handleAddUser(d, "consignee")} />
      )}
      {modal === "vehicle" && (
        <AddModal title="Vehicle"
          fields={[
            { key: "vehicle_number", label: "Vehicle Number", required: true },
            { key: "vehicle_type", label: "Vehicle Type (Truck/Tempo etc.)" },
          ]}
          onClose={() => setModal(null)} onSave={handleAddVehicle} />
      )}
    </div>
  );
};

export default EditConsignment;